// localStorage wrapper for the user's selected audio I/O devices.
//
// Device preferences are environment-specific (the AirPods are only paired
// on one laptop, the desk mic is on another), so we deliberately keep them
// out of the server-side Settings KV. The Settings.lastInputId /
// lastOutputId fields still exist on the type for forward compatibility
// but are @deprecated in 2026-05 per Phase 3 — never read, never written.

const INPUT_KEY = "koe.devices.lastInputId";
const OUTPUT_KEY = "koe.devices.lastOutputId";

export interface SelectedDevices {
  inputId: string | null;
  outputId: string | null;
}

function readKey(key: string): string | null {
  try {
    const raw = window.localStorage.getItem(key);
    return raw && raw.length > 0 ? raw : null;
  } catch {
    return null;
  }
}

function writeKey(key: string, value: string | null): void {
  try {
    if (value && value.length > 0) {
      window.localStorage.setItem(key, value);
    } else {
      window.localStorage.removeItem(key);
    }
  } catch {
    /* ignore quota / private browsing */
  }
}

export function readSelectedDevices(): SelectedDevices {
  return {
    inputId: readKey(INPUT_KEY),
    outputId: readKey(OUTPUT_KEY),
  };
}

export function writeSelectedInput(id: string | null): void {
  writeKey(INPUT_KEY, id);
}

export function writeSelectedOutput(id: string | null): void {
  writeKey(OUTPUT_KEY, id);
}

/** Placeholder "use the system default" sentinel.
 *  We store this as `null` in localStorage, but emit it as a stable option
 *  value in the Settings panel <select>. Using an empty string would let
 *  the option collide with a browser-generated anonymous deviceId. */
export const SYSTEM_DEFAULT_DEVICE_ID = "__system_default__";
